import classNames from "classnames";
import React from "react";
import type { ColumnMetaData } from "./ColumnMetaData";
import useDistinctShape from "../lib/hooks/useDistinctShape";
import Td, { INTERACTIVE_CLASSNAME } from "./Td";
import Tr from "./Tr";

interface P<Data, Column extends string> {
  data: Data;
  metadata: { [K in Column]: ColumnMetaData<Data> };
  onRowClick?: (data: Data, e: React.MouseEvent<HTMLTableRowElement>) => void;
  onRowMouseEnter?: (data: Data, e: React.MouseEvent<HTMLTableRowElement>) => void;
  onRowMouseLeave?: (data: Data, e: React.MouseEvent<HTMLTableRowElement>) => void;
  isRowInteractive?: (data: Data) => boolean;
  columns: Column[];
  onSelection: (() => void) | null;
  isRowSelected: (data: Data) => boolean;
  dataKey: string;
}

const Row = <Data, Column extends string>({
  isRowInteractive,
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave,
  columns,
  metadata,
  data,
  dataKey,
  onSelection,
  isRowSelected,
}: P<Data, Column>) => {
  const interactive = isRowInteractive?.(data) ?? Boolean(onRowClick);
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLTableRowElement>) => {
      if (interactive && onRowClick) {
        // check that click does not come from a modal (React propagate portal clicks to parent)
        const realTrClick =
          e.currentTarget &&
          e.target &&
          (e.currentTarget === e.target || e.currentTarget.contains(e.target as Node));
        if (realTrClick) onRowClick(data, e);
      }
    },
    [data, interactive, onRowClick],
  );

  const handleMouseEnter = React.useCallback(
    (e: React.MouseEvent<HTMLTableRowElement>) => {
      e.stopPropagation();
      onRowMouseEnter?.(data, e);
    },
    [data, onRowMouseEnter],
  );

  const handleMouseLeave = React.useCallback(
    (e: React.MouseEvent<HTMLTableRowElement>) => {
      e.stopPropagation();
      onRowMouseLeave?.(data, e);
    },
    [data, onRowMouseLeave],
  );

  return (
    <Tr
      data-key={dataKey} // needed for form
      data-cy-row-key={dataKey}
      onClick={handleClick}
      onMouseEnter={onRowMouseEnter ? handleMouseEnter : undefined}
      onMouseLeave={onRowMouseLeave ? handleMouseLeave : undefined}
      className={classNames({
        [INTERACTIVE_CLASSNAME]: interactive,
      })}
    >
      {columns.map((column, index) => (
        <Td<Data>
          key={column}
          data-column={column}
          onSelection={index === 0 ? onSelection : null}
          isRowSelected={isRowSelected}
          ellipsis={metadata[column].ellipsis}
          alignment={metadata[column].alignment}
          verticalAlignmentCenter={metadata[column].verticalAlignmentCenter}
          minWidth={metadata[column].minWidth}
          maxWidth={metadata[column].maxWidth}
          width={metadata[column].width}
          showEmptyMark={metadata[column].showEmptyMark}
          sticky={metadata[column].sticky}
          stickyDirection={metadata[column].stickyDirection}
          stickyBorder={metadata[column].stickyBorder}
          verticalSeparator={Boolean(metadata[column].verticalSeparator)}
          render={metadata[column].render}
          data={data}
          rowKey={dataKey}
        />
      ))}
    </Tr>
  );
};

const MemoRow = React.memo(Row) as typeof Row;
const RowProxy = <Data, Column extends string>({ data, ...props }: P<Data, Column>) => {
  const stableData = useDistinctShape(data);
  return <MemoRow {...props} data={stableData} />;
};

export default RowProxy;
